<template>
  <div class="min-h-screen antialiased font-inter text-gray-brand-900 bg-gray-brand-100 flex flex-col justify-between">
    <div>
      <div v-if="showImpersonationSpacer" :class="IMPERSONATION_BANNER_HEIGHT_CLASS" />
      <router-view />
    </div>
    <template v-if="!bareLayout">
      <MerchantFooter />
      <LoggedInAs />
      <MarketingPopup @banner-received="onBanner" />
      <BannerStrip :banner="banner" />
    </template>
    <!-- One toast stack for the whole app; useToasts() is a module singleton, so any view can
         raise a toast without mounting a host of its own. -->
    <ToastHost />
  </div>
</template>

<script>
import { ref } from 'vue'
import LoggedInAs from '@/components/Profile/LoggedInAs'
import { mapGetters } from 'vuex'
import MerchantFooter from '@/components/Merchant/MerchantFooter'
import MarketingPopup from '@/components/Merchant/MarketingPopup'
import BannerStrip from '@/components/Merchant/BannerStrip'
import ToastHost from '@/components/Molecules/ToastHost'
import { IMPERSONATION_BANNER_HEIGHT_CLASS } from '@/components/Reseller/impersonationBannerHeight'
import { isSamePathNavigation } from '@/utils/navigation'
import mitt from 'mitt'

// Pages whose MerchantNav sits in normal flow (not `fixed`) already push
// content down by the banner's height, so they need no spacer.
const IN_FLOW_NAV_PATHS = [/^\/p\//, /^\/reseller/]

const BRAND_THEME_PROPERTIES = ['--main-brand-hue', '--main-brand-saturation', '--main-brand-lightness']
const toChannel = (value) => (value === '' || value == null ? NaN : Number(value))

export default {
  components: { LoggedInAs, MerchantFooter, MarketingPopup, BannerStrip, ToastHost },
  data() {
    return { banner: null, headTagsInjected: false, leftLandingPage: false, IMPERSONATION_BANNER_HEIGHT_CLASS }
  },
  computed: {
    ...mapGetters(['info', 'impersonatingReseller']),
    bareLayout() {
      return Boolean(this.$route.meta?.bareLayout)
    },
    showImpersonationSpacer() {
      if (this.bareLayout) return false
      return this.impersonatingReseller && !IN_FLOW_NAV_PATHS.some((re) => re.test(this.$route.path))
    },
    hsl() {
      return this.info?.attributes?.color?.hsl
    },
    title() {
      return [this.capitalizedHandle, ' ', 'Checkout'].join('')
    },
    facebookPixelContainerID() {
      return this.info?.attributes?.facebook_pixel_container_id
    },
    capitalizedHandle() {
      if (this.info?.attributes?.handle) {
        return this.info?.attributes?.handle?.charAt(0)?.toUpperCase() + this.info?.attributes?.handle?.slice(1)
      } else {
        return ''
      }
    }
  },
  watch: {
    $route(to, from) {
      if (this.leftLandingPage || !from.matched.length || isSamePathNavigation(to, from)) return
      this.leftLandingPage = true
      this.setTitle()
    },
    info: function (newInfo, oldInfo) {
      if (newInfo) {
        this.setTitle()
        this.setFavicon()
        if (!this.headTagsInjected) {
          this.headTagsInjected = true
          this.setCustomHeadTags()
          this.setGTM()
        }
        if (this.facebookPixelContainerID) {
          this.configureFacebookPixel()
        }
      }
    },

    hsl: {
      immediate: true,
      handler(value) {
        this.applyBrandTheme(value)
      }
    }
  },
  mounted() {
    this.initHooks()
    this.$store.dispatch('initializeCurrentTokens')
    this.$store.dispatch('getInfo').then(() => {
      // Franchise root domains resolve no merchant on first load (BOT-1295),
      // so info stays empty and the watcher never runs — inject Bottle's own
      // GTM anyway so those landings are still captured. setGTM dedupes, so
      // the watcher re-running it later is a no-op.
      if (!this.info) this.setGTM()
    })
    this.setTitle()
  },
  methods: {
    // index.css derives every brand-* shade from these three values at :root, and Overlay and
    // ToastHost Teleport to <body>, so the document element is the one scope the whole page and
    // every teleported surface resolve. Removing the properties restores the stylesheet default.
    applyBrandTheme(hsl) {
      if (typeof document === 'undefined') return

      const style = document.documentElement.style
      const [hue, saturation, lightness] = Array.isArray(hsl) ? hsl.map(toChannel) : []
      if (![hue, saturation, lightness].every(Number.isFinite)) {
        BRAND_THEME_PROPERTIES.forEach((name) => style.removeProperty(name))
        return
      }
      style.setProperty('--main-brand-hue', String(hue))
      style.setProperty('--main-brand-saturation', `${saturation}%`)
      style.setProperty('--main-brand-lightness', `${lightness}%`)
    },

    setTitle() {
      const served = document.querySelector('title[data-server-rendered]')
      if (served && !this.leftLandingPage) return
      if (served) served.removeAttribute('data-server-rendered')
      document.title = this.title
    },
    setFavicon() {
      const url = this.info?.attributes?.favicon_url
      if (!url) return
      let link = document.querySelector("link[rel~='icon']")
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'icon')
        document.head.appendChild(link)
      }
      link.setAttribute('type', 'image/png')
      link.setAttribute('href', url)
    },
    initHooks() {
      window.bottle = { hooks: { v1: mitt() } }
    },
    setCustomHeadTags() {
      let wrapper = document.createElement('div')
      wrapper.innerHTML = this.info?.attributes?.html_head_content
      const wrapperChildrenAsArray = [].slice.call(wrapper.children)
      wrapperChildrenAsArray.forEach((item) => {
        let newScriptElement = document.createElement(item.nodeName.toLowerCase())
        let existingAttributes = item.getAttributeNames()
        newScriptElement.innerHTML = item.innerHTML
        existingAttributes.forEach((attr) => {
          newScriptElement.setAttribute(attr, item.getAttribute(attr))
        })
        document.head.appendChild(newScriptElement)
      })
      // Store a marker to ensure our GTM is added after merchant content
      const marker = document.createComment('Bottle GTM marker')
      document.head.appendChild(marker)
      return marker
    },
    setGTM() {
      // Add Bottle GTM script to head
      if (process.env.NODE_ENV !== 'production') {
        return
      }

      if (document.querySelector('script[data-gtm-id="GTM-5KMZQ29Q"]')) {
        return
      }

      const gtmScript = document.createElement('script')
      gtmScript.setAttribute('data-gtm-id', 'GTM-5KMZQ29Q')
      gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-5KMZQ29Q');`

      const marker = Array.from(document.head.childNodes).find(
        (node) => node.nodeType === Node.COMMENT_NODE && node.textContent === 'Bottle GTM marker'
      )
      if (marker) {
        document.head.insertBefore(gtmScript, marker.nextSibling)
      } else {
        document.head.appendChild(gtmScript)
      }

      const gtmNoscript = document.createElement('noscript')
      const gtmIframe = document.createElement('iframe')
      gtmIframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-5KMZQ29Q'
      gtmIframe.height = '0'
      gtmIframe.width = '0'
      gtmIframe.style.display = 'none'
      gtmIframe.style.visibility = 'hidden'
      gtmNoscript.appendChild(gtmIframe)
      document.body.insertBefore(gtmNoscript, document.body.firstChild)
    },
    configureFacebookPixel() {
      window.fbq('init', this.facebookPixelContainerID)
      if (this.$store.state.facebookPixelInitialized) return
      this.$store.commit('SET_FACEBOOK_PIXEL_INITIALIZED', true)
      // With cached info this runs before the initial navigation resolves
      // ($route is still the start location, matched is empty) — the router
      // guard sees the flag and fires the landing PageView itself. Firing here
      // too would double-count it and stamp the pre-navigation URL.
      if (this.$route.matched.length) {
        window.fbq('track', 'PageView', { URL: this.$route.fullPath })
      }
    },
    onBanner(b) {
      this.banner = b
    }
  }
}
</script>

<style lang="scss">
html,
body,
#app {
  height: 100%;
}
// input:focus,
// button:focus,
// textarea:focus {
//   outline: none !important;
//   box-shadow: 0 0 0 0.15rem #025bc4;
//   transition-duration: 0.15s;
// }
/* Hide scrollbar for Chrome, Safari and Opera */
.scrollbars-hidden::-webkit-scrollbar {
  display: none;
}
/* Hide scrollbar for IE and Edge */
.scrollbars-hidden {
  -ms-overflow-style: none;
}
</style>
